import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Snackbar,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { 
  Refresh as RefreshIcon, 
  CheckCircle as ActiveIcon, 
  Cancel as InactiveIcon,
  Edit as EditIcon 
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface Province {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  zone?: number;
  is_active: boolean;
  is_deleted: boolean;
}

interface District {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  name_short: Array<{ code: string; name: string }>;
  en_name: string;
  areacode: number;
  province_code: number;
  is_active: boolean;
  is_deleted: boolean;
}

interface SubDistrict {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  name_short: Array<{ code: string; name: string }>;
  en_name: string;
  areacode: number;
  district_code: number;
  province_code: number;
  zipcode?: string;
  is_active: boolean;
  is_deleted: boolean;
}

interface Degree {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  level: number;
  is_active: boolean;
}

interface Faculty {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  abbreviation_th?: string;
  abbreviation_en?: string;
  is_active: boolean;
}

interface Major {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  faculty_code: string;
  degree_code: string;
  is_active: boolean;
}

function MasterDataManagement() {
  const [tabValue, setTabValue] = useState(0);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [subDistricts, setSubDistricts] = useState<SubDistrict[]>([]);
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  const [majors, setMajors] = useState<Major[]>([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  
  // Edit dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSubDistrict, setEditingSubDistrict] = useState<SubDistrict | null>(null);
  const [editZipcode, setEditZipcode] = useState('');
  
  // Education CRUD dialog states
  const [degreeDialogOpen, setDegreeDialogOpen] = useState(false);
  const [facultyDialogOpen, setFacultyDialogOpen] = useState(false);
  const [majorDialogOpen, setMajorDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingDegree, setEditingDegree] = useState<Degree | null>(null);
  const [editingFaculty, setEditingFaculty] = useState<Faculty | null>(null);
  const [editingMajor, setEditingMajor] = useState<Major | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  
  const [degreeForm, setDegreeForm] = useState({ code: '', name_th: '', name_en: '', level: 1 });
  const [facultyForm, setFacultyForm] = useState({ code: '', name_th: '', name_en: '', abbreviation_th: '', abbreviation_en: '' });
  const [majorForm, setMajorForm] = useState({ code: '', name_th: '', name_en: '', faculty_code: '', degree_code: '' });
  
  // Filter states
  const [provinceFilter, setProvinceFilter] = useState({ name: '', is_active: 'all' });
  const [districtFilter, setDistrictFilter] = useState({ name: '', province_code: '', is_active: 'all' });
  const [subDistrictFilter, setSubDistrictFilter] = useState({ name: '', district_code: '', province_code: '', is_active: 'all' });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
    // Fetch provinces for filter dropdowns if not already loaded
    if (provinces.length === 0 && tabValue !== 0) {
      fetchProvincesForFilter();
    }
    // Fetch districts for sub-district filter
    if (districts.length === 0 && tabValue === 2) {
      fetchDistrictsForFilter();
    }
  }, [tabValue]);

  // Auto-filter when filter values change (with debounce for name search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tabValue === 0) {
        fetchData();
      }
    }, 500); // 500ms debounce for name search

    return () => clearTimeout(timeoutId);
  }, [provinceFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tabValue === 1) {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [districtFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (tabValue === 2) {
        fetchData();
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [subDistrictFilter]);

  const fetchProvincesForFilter = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/master-data/provinces`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const mappedData = response.data.map((item: any) => ({
        ...item,
        id: item._id || item.id
      }));
      setProvinces(mappedData);
    } catch (error) {
      console.error('Failed to fetch provinces for filter');
    }
  };

  const fetchDistrictsForFilter = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/master-data/districts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const mappedData = response.data.map((item: any) => ({
        ...item,
        id: item._id || item.id
      }));
      setDistricts(mappedData);
    } catch (error) {
      console.error('Failed to fetch districts for filter');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        const params: any = {};
        if (provinceFilter.name) params.name = provinceFilter.name;
        if (provinceFilter.is_active !== 'all') params.is_active = provinceFilter.is_active === 'true';
        
        const response = await axios.get(`${API_URL}/api/v1/master-data/provinces`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setProvinces(mappedData);
      } else if (tabValue === 1) {
        const params: any = {};
        if (districtFilter.name) params.name = districtFilter.name;
        if (districtFilter.province_code) params.province_code = parseInt(districtFilter.province_code);
        if (districtFilter.is_active !== 'all') params.is_active = districtFilter.is_active === 'true';
        
        const response = await axios.get(`${API_URL}/api/v1/master-data/districts`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setDistricts(mappedData);
      } else if (tabValue === 2) {
        const params: any = {};
        if (subDistrictFilter.name) params.name = subDistrictFilter.name;
        if (subDistrictFilter.district_code) params.district_code = parseInt(subDistrictFilter.district_code);
        if (subDistrictFilter.province_code) params.province_code = parseInt(subDistrictFilter.province_code);
        if (subDistrictFilter.is_active !== 'all') params.is_active = subDistrictFilter.is_active === 'true';
        
        // When filters are applied, use smaller limit for faster loading
        const hasFilters = subDistrictFilter.name || subDistrictFilter.district_code || subDistrictFilter.province_code || subDistrictFilter.is_active !== 'all';
        params.skip = 0;
        params.limit = hasFilters ? 5000 : 10000;
        
        const response = await axios.get(`${API_URL}/api/v1/master-data/sub-districts`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        
        // Handle new paginated response format
        const data = response.data.items || response.data;
        const mappedData = data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setSubDistricts(mappedData);
      } else if (tabValue === 3) {
        // Fetch Degrees
        const response = await axios.get(`${API_URL}/api/v1/master-data/degrees`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setDegrees(mappedData);
      } else if (tabValue === 4) {
        // Fetch Faculties
        const response = await axios.get(`${API_URL}/api/v1/master-data/faculties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setFaculties(mappedData);
      } else if (tabValue === 5) {
        // Fetch Majors
        const response = await axios.get(`${API_URL}/api/v1/master-data/majors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const mappedData = response.data.map((item: any) => ({
          ...item,
          id: item._id || item.id
        }));
        setMajors(mappedData);
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to fetch data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubDistrict = (subDistrict: SubDistrict) => {
    setEditingSubDistrict(subDistrict);
    setEditZipcode(subDistrict.zipcode || '');
    setEditDialogOpen(true);
  };

  const handleSaveZipcode = async () => {
    if (!editingSubDistrict) return;

    try {
      const subDistrictId = (editingSubDistrict as any).id || editingSubDistrict._id;
      await axios.patch(
        `${API_URL}/api/v1/master-data/sub-districts/${subDistrictId}`,
        { zipcode: editZipcode || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbar({
        open: true,
        message: 'Zipcode updated successfully',
        severity: 'success'
      });
      
      setEditDialogOpen(false);
      setEditingSubDistrict(null);
      setEditZipcode('');
      fetchData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update zipcode',
        severity: 'error'
      });
    }
  };

  const toggleActiveStatus = async (type: 'province' | 'district' | 'sub-district' | 'degree' | 'faculty' | 'major', id: string, currentStatus: boolean) => {
    try {
      let endpoint = '';
      if (type === 'province') endpoint = 'provinces';
      else if (type === 'district') endpoint = 'districts';
      else if (type === 'sub-district') endpoint = 'sub-districts';
      else if (type === 'degree') endpoint = 'degrees';
      else if (type === 'faculty') endpoint = 'faculties';
      else if (type === 'major') endpoint = 'majors';
      
      await axios.patch(
        `${API_URL}/api/v1/master-data/${endpoint}/${id}/active`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbar({
        open: true,
        message: `${type} status updated successfully`,
        severity: 'success'
      });
      
      fetchData();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.detail || 'Failed to update status',
        severity: 'error'
      });
    }
  };

  const clearProvinceFilter = () => {
    setProvinceFilter({ name: '', is_active: 'all' });
  };

  const clearDistrictFilter = () => {
    setDistrictFilter({ name: '', province_code: '', is_active: 'all' });
  };

  const clearSubDistrictFilter = () => {
    setSubDistrictFilter({ name: '', district_code: '', province_code: '', is_active: 'all' });
  };

  // Education CRUD Handlers
  const handleCreateDegree = () => {
    setEditingDegree(null);
    setDegreeForm({ code: '', name_th: '', name_en: '', level: 1 });
    setDegreeDialogOpen(true);
  };

  const handleEditDegree = (degree: Degree) => {
    setEditingDegree(degree);
    setDegreeForm({ 
      code: degree.code, 
      name_th: degree.name_th, 
      name_en: degree.name_en, 
      level: degree.level 
    });
    setDegreeDialogOpen(true);
  };

  const handleSaveDegree = async () => {
    try {
      if (editingDegree) {
        // Update
        const degreeId = (editingDegree as any).id || editingDegree._id;
        await axios.patch(
          `${API_URL}/api/v1/master-data/degrees/${degreeId}`,
          degreeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Degree updated successfully', severity: 'success' });
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/v1/master-data/degrees`,
          degreeForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Degree created successfully', severity: 'success' });
      }
      setDegreeDialogOpen(false);
      fetchData();
    } catch (error: any) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail
        : error.response?.data?.detail?.[0]?.msg || 'Failed to save degree';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  const handleCreateFaculty = () => {
    setEditingFaculty(null);
    setFacultyForm({ code: '', name_th: '', name_en: '', abbreviation_th: '', abbreviation_en: '' });
    setFacultyDialogOpen(true);
  };

  const handleEditFaculty = (faculty: Faculty) => {
    setEditingFaculty(faculty);
    setFacultyForm({ 
      code: faculty.code, 
      name_th: faculty.name_th, 
      name_en: faculty.name_en, 
      abbreviation_th: faculty.abbreviation_th || '', 
      abbreviation_en: faculty.abbreviation_en || '' 
    });
    setFacultyDialogOpen(true);
  };

  const handleSaveFaculty = async () => {
    try {
      if (editingFaculty) {
        // Update
        const facultyId = (editingFaculty as any).id || editingFaculty._id;
        await axios.patch(
          `${API_URL}/api/v1/master-data/faculties/${facultyId}`,
          facultyForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Faculty updated successfully', severity: 'success' });
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/v1/master-data/faculties`,
          facultyForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Faculty created successfully', severity: 'success' });
      }
      setFacultyDialogOpen(false);
      fetchData();
    } catch (error: any) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail
        : error.response?.data?.detail?.[0]?.msg || 'Failed to save faculty';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  const handleCreateMajor = () => {
    setEditingMajor(null);
    setMajorForm({ code: '', name_th: '', name_en: '', faculty_code: '', degree_code: '' });
    setMajorDialogOpen(true);
  };

  const handleEditMajor = (major: Major) => {
    setEditingMajor(major);
    setMajorForm({ 
      code: major.code, 
      name_th: major.name_th, 
      name_en: major.name_en, 
      faculty_code: major.faculty_code, 
      degree_code: major.degree_code 
    });
    setMajorDialogOpen(true);
  };

  const handleSaveMajor = async () => {
    try {
      if (editingMajor) {
        // Update
        const majorId = (editingMajor as any).id || editingMajor._id;
        await axios.patch(
          `${API_URL}/api/v1/master-data/majors/${majorId}`,
          majorForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Major updated successfully', severity: 'success' });
      } else {
        // Create
        await axios.post(
          `${API_URL}/api/v1/master-data/majors`,
          majorForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSnackbar({ open: true, message: 'Major created successfully', severity: 'success' });
      }
      setMajorDialogOpen(false);
      fetchData();
    } catch (error: any) {
      const errorMessage = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail
        : error.response?.data?.detail?.[0]?.msg || 'Failed to save major';
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    }
  };

  // Delete functionality - currently not exposed in UI to prevent accidental deletion of data referenced by alumni
  // Uncomment and add Delete buttons to column actions if needed
  /*
  const handleDeleteConfirm = (type: string, id: string, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialogOpen(true);
  };
  */

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      let endpoint = '';
      if (deleteTarget.type === 'degree') endpoint = 'degrees';
      else if (deleteTarget.type === 'faculty') endpoint = 'faculties';
      else if (deleteTarget.type === 'major') endpoint = 'majors';
      
      await axios.delete(
        `${API_URL}/api/v1/master-data/${endpoint}/${deleteTarget.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSnackbar({ 
        open: true, 
        message: `${deleteTarget.type} deleted successfully`, 
        severity: 'success' 
      });
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to delete', 
        severity: 'error' 
      });
    }
  };



  const provinceColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', flex: 0.5, minWidth: 80 },
    { 
      field: 'name_th', 
      headerName: 'Name (TH)', 
      flex: 1.5,
      minWidth: 200,
      valueGetter: (_value: any, row: any) => {
        const thName = row.name?.find((n: any) => n.code === 'th');
        return thName?.name || '';
      }
    },
    { field: 'en_name', headerName: 'Name (EN)', flex: 1.5, minWidth: 200 },
    { field: 'zone', headerName: 'Zone', flex: 0.5, minWidth: 80 },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      flex: 0.5,
      minWidth: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: any) => [
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('province', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const districtColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', flex: 0.5, minWidth: 80 },
    { 
      field: 'name_th', 
      headerName: 'Name (TH)', 
      flex: 1.2,
      minWidth: 180,
      valueGetter: (_value: any, row: any) => {
        const thName = row.name?.find((n: any) => n.code === 'th');
        return thName?.name || '';
      }
    },
    { field: 'en_name', headerName: 'Name (EN)', flex: 1.2, minWidth: 180 },
    { 
      field: 'province_name', 
      headerName: 'Province', 
      flex: 1.2,
      minWidth: 180,
      valueGetter: (_value: any, row: any) => {
        const province = provinces.find(p => p.code === row.province_code);
        if (!province) return `Code: ${row.province_code}`;
        const thName = province.name?.find((n: any) => n.code === 'th');
        return thName?.name || province.en_name || `Code: ${row.province_code}`;
      }
    },
    { field: 'areacode', headerName: 'Area Code', flex: 0.6, minWidth: 100 },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      flex: 0.5,
      minWidth: 90,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params: any) => [
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('district', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const subDistrictColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', flex: 0.5, minWidth: 80 },
    { 
      field: 'name_th', 
      headerName: 'Name (TH)', 
      flex: 1.2,
      minWidth: 180,
      valueGetter: (_value: any, row: any) => {
        const thName = row.name?.find((n: any) => n.code === 'th');
        return thName?.name || '';
      }
    },
    { field: 'en_name', headerName: 'Name (EN)', flex: 1.2, minWidth: 180 },
    { field: 'zipcode', headerName: 'Postal Code', width: 110 },
    { 
      field: 'district_name', 
      headerName: 'District', 
      flex: 1,
      minWidth: 160,
      valueGetter: (_value: any, row: any) => {
        const district = districts.find(d => d.code === row.district_code);
        if (!district) return `Code: ${row.district_code}`;
        const thName = district.name?.find((n: any) => n.code === 'th');
        return thName?.name || district.en_name || `Code: ${row.district_code}`;
      }
    },
    { 
      field: 'province_name', 
      headerName: 'Province', 
      flex: 1,
      minWidth: 160,
      valueGetter: (_value: any, row: any) => {
        const province = provinces.find(p => p.code === row.province_code);
        if (!province) return `Code: ${row.province_code}`;
        const thName = province.name?.find((n: any) => n.code === 'th');
        return thName?.name || province.en_name || `Code: ${row.province_code}`;
      }
    },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: any) => [
        <Tooltip key="edit" title="Edit Zipcode">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditSubDistrict(params.row)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('sub-district', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const degreeColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name_en', headerName: 'Name (EN)', flex: 1, minWidth: 250 },
    { field: 'name_th', headerName: 'Name (TH)', flex: 1, minWidth: 250 },
    { field: 'level', headerName: 'Level', width: 100 },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: any) => [
        <Tooltip key="edit" title="Edit">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditDegree(params.row)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('degree', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const facultyColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name_en', headerName: 'Name (EN)', flex: 1, minWidth: 250 },
    { field: 'name_th', headerName: 'Name (TH)', flex: 1, minWidth: 250 },
    { field: 'abbreviation_en', headerName: 'Abbr. (EN)', width: 120 },
    { field: 'abbreviation_th', headerName: 'Abbr. (TH)', width: 120 },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: any) => [
        <Tooltip key="edit" title="Edit">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditFaculty(params.row)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('faculty', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  const majorColumns: GridColDef[] = [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name_en', headerName: 'Name (EN)', flex: 1, minWidth: 250 },
    { field: 'name_th', headerName: 'Name (TH)', flex: 1, minWidth: 250 },
    { 
      field: 'faculty_name', 
      headerName: 'Faculty', 
      width: 200,
      valueGetter: (_value: any, row: any) => {
        const faculty = faculties.find(f => f.code === row.faculty_code);
        return faculty ? faculty.name_en : row.faculty_code;
      }
    },
    { 
      field: 'degree_name', 
      headerName: 'Degree', 
      width: 150,
      valueGetter: (_value: any, row: any) => {
        const degree = degrees.find(d => d.code === row.degree_code);
        return degree ? degree.name_en : row.degree_code;
      }
    },
    { 
      field: 'is_active', 
      headerName: 'Active', 
      width: 100,
      type: 'boolean'
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params: any) => [
        <Tooltip key="edit" title="Edit">
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleEditMajor(params.row)}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>,
        <Tooltip key="toggle" title={params.row.is_active ? 'Deactivate' : 'Activate'}>
          <IconButton
            color={params.row.is_active ? 'error' : 'success'}
            size="small"
            onClick={() => toggleActiveStatus('major', params.row.id, params.row.is_active)}
          >
            {params.row.is_active ? <InactiveIcon /> : <ActiveIcon />}
          </IconButton>
        </Tooltip>
      ]
    }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2 }}>
        <Typography variant="h4" component="h1">
          Master Data Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ mx: 3, mb: 3, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }} variant="scrollable" scrollButtons="auto">
          <Tab key="provinces" label="Provinces" />
          <Tab key="districts" label="Districts" />
          <Tab key="subdistricts" label="Sub-Districts" />
          <Tab key="degrees" label="Degrees" />
          <Tab key="faculties" label="Faculties" />
          <Tab key="majors" label="Majors" />
        </Tabs>

        <Box sx={{ flex: 1, p: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {tabValue === 0 && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  size="small"
                  label="Search by Name"
                  value={provinceFilter.name}
                  onChange={(e) => setProvinceFilter({ ...provinceFilter, name: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={provinceFilter.is_active}
                  onChange={(e) => setProvinceFilter({ ...provinceFilter, is_active: e.target.value })}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={clearProvinceFilter}>Clear Filter</Button>
              </Stack>
              <DataGrid
                rows={provinces}
                columns={provinceColumns}
                loading={loading}
                pagination
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableVirtualization={false}
                sx={{ 
                  height: '100%', 
                  width: '100%',
                  '& .MuiDataGrid-virtualScroller': {
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }
                }}
              />
            </>
          )}

          {tabValue === 1 && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Search by Name"
                  value={districtFilter.name}
                  onChange={(e) => setDistrictFilter({ ...districtFilter, name: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  select
                  size="small"
                  label="Province"
                  value={districtFilter.province_code}
                  onChange={(e) => setDistrictFilter({ ...districtFilter, province_code: e.target.value })}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">All Provinces</MenuItem>
                  {provinces.map((p) => (
                    <MenuItem key={p.code} value={p.code.toString()}>
                      {p.name?.find((n: any) => n.code === 'th')?.name || p.en_name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={districtFilter.is_active}
                  onChange={(e) => setDistrictFilter({ ...districtFilter, is_active: e.target.value })}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={clearDistrictFilter}>Clear Filter</Button>
              </Stack>
              <DataGrid
                rows={districts}
                columns={districtColumns}
                loading={loading}
                pagination
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableVirtualization={false}
                sx={{ 
                  height: '100%', 
                  width: '100%',
                  '& .MuiDataGrid-virtualScroller': {
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }
                }}
              />
            </>
          )}

          {tabValue === 2 && (
            <>
              <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  label="Search by Name"
                  value={subDistrictFilter.name}
                  onChange={(e) => setSubDistrictFilter({ ...subDistrictFilter, name: e.target.value })}
                  sx={{ minWidth: 200 }}
                />
                <TextField
                  select
                  size="small"
                  label="Province"
                  value={subDistrictFilter.province_code}
                  onChange={(e) => setSubDistrictFilter({ ...subDistrictFilter, province_code: e.target.value, district_code: '' })}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">All Provinces</MenuItem>
                  {provinces.map((p) => (
                    <MenuItem key={p.code} value={p.code.toString()}>
                      {p.name?.find((n: any) => n.code === 'th')?.name || p.en_name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="District"
                  value={subDistrictFilter.district_code}
                  onChange={(e) => setSubDistrictFilter({ ...subDistrictFilter, district_code: e.target.value })}
                  sx={{ minWidth: 180 }}
                  disabled={!subDistrictFilter.province_code}
                >
                  <MenuItem value="">All Districts</MenuItem>
                  {districts
                    .filter(d => !subDistrictFilter.province_code || d.province_code.toString() === subDistrictFilter.province_code)
                    .map((d) => (
                      <MenuItem key={d.code} value={d.code.toString()}>
                        {d.name?.find((n: any) => n.code === 'th')?.name || d.en_name}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="Status"
                  value={subDistrictFilter.is_active}
                  onChange={(e) => setSubDistrictFilter({ ...subDistrictFilter, is_active: e.target.value })}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </TextField>
                <Button variant="outlined" onClick={clearSubDistrictFilter}>Clear Filter</Button>
              </Stack>
              <DataGrid
                rows={subDistricts}
                columns={subDistrictColumns}
                loading={loading}
                pagination
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                disableVirtualization={false}
                sx={{ 
                  height: '100%', 
                  width: '100%',
                  '& .MuiDataGrid-virtualScroller': {
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                  }
                }}
              />
            </>
          )}

          {/* Tab 3: Degrees */}
          {tabValue === 3 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={handleCreateDegree}>
                  Add New Degree
                </Button>
              </Box>
              <DataGrid
                rows={degrees}
                columns={degreeColumns}
                loading={loading}
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={{ 
                  height: '100%', 
                  width: '100%'
                }}
              />
            </>
          )}

          {/* Tab 4: Faculties */}
          {tabValue === 4 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={handleCreateFaculty}>
                  Add New Faculty
                </Button>
              </Box>
              <DataGrid
                rows={faculties}
                columns={facultyColumns}
                loading={loading}
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={{ 
                  height: '100%', 
                  width: '100%'
                }}
              />
            </>
          )}

          {/* Tab 5: Majors */}
          {tabValue === 5 && (
            <>
              <Box sx={{ mb: 2 }}>
                <Button variant="contained" onClick={handleCreateMajor}>
                  Add New Major
                </Button>
              </Box>
              <DataGrid
                rows={majors}
                columns={majorColumns}
                loading={loading}
                paginationMode="client"
                autoHeight={false}
                slots={{ toolbar: GridToolbar }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={{ 
                  height: '100%', 
                  width: '100%'
                }}
              />
            </>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Sub-District Zipcode Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Postal Code
        </DialogTitle>
        <DialogContent>
          {editingSubDistrict && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Sub-District: {editingSubDistrict.name?.find((n: any) => n.code === 'th')?.name || editingSubDistrict.en_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Code: {editingSubDistrict.code}
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Postal Code (5 digits)"
                type="text"
                fullWidth
                value={editZipcode}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow digits and max 5 characters
                  if (/^\d{0,5}$/.test(value)) {
                    setEditZipcode(value);
                  }
                }}
                inputProps={{
                  maxLength: 5,
                  pattern: "[0-9]{5}"
                }}
                helperText="Enter 5-digit Thai postal code or leave empty"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveZipcode} 
            variant="contained"
            disabled={editZipcode.length > 0 && editZipcode.length !== 5}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Degree Create/Edit Dialog */}
      <Dialog open={degreeDialogOpen} onClose={() => setDegreeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDegree ? 'Edit Degree' : 'Add New Degree'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Code *"
              fullWidth
              value={degreeForm.code}
              onChange={(e) => setDegreeForm({ ...degreeForm, code: e.target.value })}
              disabled={!!editingDegree}
            />
            <TextField
              label="Name (English) *"
              fullWidth
              value={degreeForm.name_en}
              onChange={(e) => setDegreeForm({ ...degreeForm, name_en: e.target.value })}
            />
            <TextField
              label="Name (Thai) *"
              fullWidth
              value={degreeForm.name_th}
              onChange={(e) => setDegreeForm({ ...degreeForm, name_th: e.target.value })}
            />
            <TextField
              label="Level *"
              type="number"
              fullWidth
              value={degreeForm.level}
              onChange={(e) => setDegreeForm({ ...degreeForm, level: parseInt(e.target.value) || 1 })}
              inputProps={{ min: 1, max: 10 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDegreeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveDegree} 
            variant="contained"
            disabled={!degreeForm.code || !degreeForm.name_en || !degreeForm.name_th}
          >
            {editingDegree ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Faculty Create/Edit Dialog */}
      <Dialog open={facultyDialogOpen} onClose={() => setFacultyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingFaculty ? 'Edit Faculty' : 'Add New Faculty'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Code *"
              fullWidth
              value={facultyForm.code}
              onChange={(e) => setFacultyForm({ ...facultyForm, code: e.target.value })}
              disabled={!!editingFaculty}
            />
            <TextField
              label="Name (English) *"
              fullWidth
              value={facultyForm.name_en}
              onChange={(e) => setFacultyForm({ ...facultyForm, name_en: e.target.value })}
            />
            <TextField
              label="Name (Thai) *"
              fullWidth
              value={facultyForm.name_th}
              onChange={(e) => setFacultyForm({ ...facultyForm, name_th: e.target.value })}
            />
            <TextField
              label="Abbreviation (English)"
              fullWidth
              value={facultyForm.abbreviation_en}
              onChange={(e) => setFacultyForm({ ...facultyForm, abbreviation_en: e.target.value })}
            />
            <TextField
              label="Abbreviation (Thai)"
              fullWidth
              value={facultyForm.abbreviation_th}
              onChange={(e) => setFacultyForm({ ...facultyForm, abbreviation_th: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFacultyDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveFaculty} 
            variant="contained"
            disabled={!facultyForm.code || !facultyForm.name_en || !facultyForm.name_th}
          >
            {editingFaculty ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Major Create/Edit Dialog */}
      <Dialog open={majorDialogOpen} onClose={() => setMajorDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingMajor ? 'Edit Major' : 'Add New Major'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Code *"
              fullWidth
              value={majorForm.code}
              onChange={(e) => setMajorForm({ ...majorForm, code: e.target.value })}
              disabled={!!editingMajor}
            />
            <TextField
              label="Name (English) *"
              fullWidth
              value={majorForm.name_en}
              onChange={(e) => setMajorForm({ ...majorForm, name_en: e.target.value })}
            />
            <TextField
              label="Name (Thai) *"
              fullWidth
              value={majorForm.name_th}
              onChange={(e) => setMajorForm({ ...majorForm, name_th: e.target.value })}
            />
            <TextField
              select
              label="Faculty *"
              fullWidth
              value={majorForm.faculty_code}
              onChange={(e) => setMajorForm({ ...majorForm, faculty_code: e.target.value })}
            >
              {faculties.map((faculty) => (
                <MenuItem key={faculty.code} value={faculty.code}>
                  {faculty.name_en} ({faculty.name_th})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Degree *"
              fullWidth
              value={majorForm.degree_code}
              onChange={(e) => setMajorForm({ ...majorForm, degree_code: e.target.value })}
            >
              {degrees.map((degree) => (
                <MenuItem key={degree.code} value={degree.code}>
                  {degree.name_en} ({degree.name_th})
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMajorDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveMajor} 
            variant="contained"
            disabled={!majorForm.code || !majorForm.name_en || !majorForm.name_th || !majorForm.faculty_code || !majorForm.degree_code}
          >
            {editingMajor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {deleteTarget?.type} "{deleteTarget?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default MasterDataManagement;
